import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { useSelector } from "react-redux";
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import Select from "react-select";
import { REACT_SELECT_DROPDOWN_STYLES } from '@shared/ui/organisms/OfficeDropDownList';
import { DateRangePicker } from '@shared/ui/molecules/DateRangePicker';
import {
  CarParkUtilizationReport,
  getCarParkUtilizationForOffice,
  OfficeCarParkUtilization
} from '@features/reporting/services/CarParkUtilizationService';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import Colours from '@shared/ui/atoms/Colours';
import { MountedContentCard, MountedContentCardContent } from '@shared/ui/atoms/MountedContentCard';
import { Moment } from "moment";
import moment from "moment";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { CustomToolTip } from "../components/CustomToolTip";
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';

const COMPANY_WIDE_ID = -1;

export function CarParkUtilizationReportingPage(props: Props) {
  const {t} = useTranslation();
  const offices = useSelector(selectAllOfficeEntities);
  const rootOffices = useMemo(() => offices.filter((o: OfficeEntity) => !o.parentId), [offices]);

  const officeOptions = useMemo(() => {
    const companyWide = { value: COMPANY_WIDE_ID, label: t('reports-page.car-park-utilization-company-wide') };
    const officeItems = rootOffices.map((o: OfficeEntity) => ({ value: o.id, label: o.label }));
    return [companyWide, ...officeItems];
  }, [rootOffices, t]);

  const [selectedOption, setSelectedOption] = useState<{ value: number; label: string } | null>(null);
  const office = useMemo(() => {
    if (!selectedOption || selectedOption.value === COMPANY_WIDE_ID) return undefined;
    return rootOffices.find((o: OfficeEntity) => o.id === selectedOption.value);
  }, [selectedOption, rootOffices]);
  const [fromDate, setFromDate] = useState<Moment>(moment().startOf('month'));
  const [toDate, setToDate] = useState<Moment>(moment().endOf('month'));
  const [reportData, setReportData] = useState<CarParkUtilizationReport | undefined>();
  const [loading, setLoading] = useState(false);
  const [expandedOffices, setExpandedOffices] = useState<Set<number>>(new Set());
  const [dailySortBy, setDailySortBy] = useState<'date' | 'capacity' | 'bookings' | 'utilizationPercentage'>('date');
  const [dailySortDirection, setDailySortDirection] = useState<'asc' | 'desc'>('asc');

  const isCompanyWide = selectedOption?.value === COMPANY_WIDE_ID;

  useEffect(() => {
    if (!selectedOption && officeOptions.length > 0) {
      setSelectedOption(officeOptions[0]);
    }
  }, [officeOptions, selectedOption]);

  useEffect(() => {
    if (selectedOption && fromDate && toDate) {
      setLoading(true);
      setReportData(undefined);
      getCarParkUtilizationForOffice(isCompanyWide ? undefined : office, fromDate, toDate)
        .then(setReportData)
        .finally(() => setLoading(false));
    }
  }, [selectedOption, fromDate, toDate, isCompanyWide, office]);

  const onDatesSelected = (from?: Moment, to?: Moment) => {
    if (from) setFromDate(from);
    if (to) setToDate(to);
  };

  const aggregatedDaily = useMemo(() => {
    if (!reportData?.offices) return [];
    const dateMap = new Map<string, { capacity: number; bookings: number }>();
    reportData.offices.forEach(o => {
      o.dailyBreakdown?.forEach(day => {
        const existing = dateMap.get(day.date);
        if (existing) {
          existing.capacity += day.capacity;
          existing.bookings += day.bookings;
        } else {
          dateMap.set(day.date, { capacity: day.capacity, bookings: day.bookings });
        }
      });
    });
    return Array.from(dateMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, { capacity, bookings }]) => ({
        date,
        capacity,
        bookings,
        utilizationPercentage: capacity > 0 ? Math.round((bookings / capacity) * 100) : 0,
      }));
  }, [reportData]);

  const dailyChartData = useMemo(() => {
    return aggregatedDaily.map(day => ({
      name: moment(day.date).format('DD MMM'),
      utilization: day.utilizationPercentage,
      bookings: day.bookings,
      capacity: day.capacity,
      toolTipTitle: moment(day.date).format('ddd DD MMM YYYY'),
    }));
  }, [aggregatedDaily]);

  const officeChartData = useMemo(() => {
    if (!reportData) return [];
    return reportData.offices.map(o => ({
      name: o.officeLabel,
      utilization: o.utilizationPercentage,
      bookings: o.totalBookings,
      capacity: o.capacity,
      toolTipTitle: o.officeLabel,
    }));
  }, [reportData]);

  const bottomSpacesChartData = useMemo(() => {
    if (!reportData?.offices) return [];
    return reportData.offices
      .flatMap(o => o.spaces ?? [])
      .map(space => ({
        name: space.label,
        office: space.officeLabel,
        utilization: space.utilizationPercentage,
        bookings: space.totalBookings,
        daysInRange: space.daysInRange,
        toolTipTitle: space.label,
      }))
      .sort((a, b) => a.utilization - b.utilization)
      .slice(0, 10);
  }, [reportData]);

  const toggleDailySort = useCallback((column: 'date' | 'capacity' | 'bookings' | 'utilizationPercentage') => {
    if (dailySortBy === column) {
      setDailySortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setDailySortBy(column);
      setDailySortDirection('asc');
    }
  }, [dailySortBy]);

  const sortedDaily = useMemo(() => {
    const data = [...aggregatedDaily];
    data.sort((a, b) => {
      const aVal = dailySortBy === 'date' ? a.date : a[dailySortBy];
      const bVal = dailySortBy === 'date' ? b.date : b[dailySortBy];
      if (aVal < bVal) return dailySortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return dailySortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [aggregatedDaily, dailySortBy, dailySortDirection]);

  const toggleOffice = (officeId: number | null) => {
    setExpandedOffices(prev => {
      const next = new Set(prev);
      const key = officeId ?? -999;
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <>
      <ReportingContainer>
        <GoBackButton url={"/manage/reporting"} />
        <HeadlineLarge style={{marginBottom: 16}}>{t('reports-page.car-park-utilization-report-title')}</HeadlineLarge>

        <ControlsRow>
          <ControlGroup>
            <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.car-park-utilization-office')}</BodyRegular>
            <Select options={officeOptions}
                    value={selectedOption}
                    onChange={(item: any) => setSelectedOption(item)}
                    styles={REACT_SELECT_DROPDOWN_STYLES}
                    isDisabled={loading}
                    isSearchable={false} />
          </ControlGroup>
          <DateRangeGroup>
            <DateRangePicker disabled={loading}
                             onDatesSelected={onDatesSelected}
                             fromDate={fromDate}
                             toDate={toDate} />
          </DateRangeGroup>
        </ControlsRow>

        {loading && (
          <Center>
            <LoadingSpinner hideBorder={true} />
          </Center>
        )}

        {!loading && !reportData && selectedOption && (
          <Center style={{marginTop: 32}}>
            <MountedContentCard>
              <MountedContentCardContent>
                <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 12}}>{t('reports-page.car-park-utilization-no-data')}</BodyLarge>
                <BodyRegular>{t('reports-page.car-park-utilization-no-data-description')}</BodyRegular>
              </MountedContentCardContent>
            </MountedContentCard>
          </Center>
        )}

        {!loading && reportData && (
          <>
            <SummaryCard>
              <SummaryTitle>{reportData.officeLabel}</SummaryTitle>
              <SummaryGrid>
                <SummaryItem>
                  <SummaryValue>{reportData.totalCapacity}</SummaryValue>
                  <SummaryLabel>{t('reports-page.car-park-utilization-total-capacity')}</SummaryLabel>
                </SummaryItem>
                <SummaryItem>
                  <SummaryValue>{reportData.totalBookings}</SummaryValue>
                  <SummaryLabel>{t('reports-page.car-park-utilization-total-bookings')}</SummaryLabel>
                </SummaryItem>
                <SummaryItem>
                  <SummaryValue>{reportData.daysInRange}</SummaryValue>
                  <SummaryLabel>{t('reports-page.car-park-utilization-days-in-range')}</SummaryLabel>
                </SummaryItem>
                <SummaryItem>
                  <SummaryValue>{reportData.utilizationPercentage}%</SummaryValue>
                  <SummaryLabel>{t('reports-page.car-park-utilization-utilization')}</SummaryLabel>
                </SummaryItem>
              </SummaryGrid>
            </SummaryCard>

            {dailyChartData.length > 0 && (
              <FlatContentCard style={{marginBottom: 24}}>
                <BodyLarge weight={600} style={{marginBottom: 12}}>{t('reports-page.car-park-utilization-daily-utilization')}</BodyLarge>
                <div style={{height: 350}}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyChartData}
                               margin={{top: 20, right: 20, left: 0, bottom: 5}}>
                      <CartesianGrid stroke={Colours.lightGrey} strokeDasharray="5 5" />
                      <XAxis dataKey="name"
                             fontFamily={'"soleil", sans-serif'}
                             fill={Colours.darkGrey}
                             fontSize={12}
                             axisLine={false}
                             tickLine={false} />
                      <YAxis fontFamily={'"soleil", sans-serif'}
                             fontSize={12}
                             axisLine={false}
                             tickLine={false}
                             domain={[0, 100]}
                             tickFormatter={(v) => `${v}%`} />
                      <Tooltip content={<CustomToolTip />} />
                      <Line type="monotone"
                            dataKey="utilization"
                            stroke={Colours.green}
                            strokeWidth={2}
                            dot={{fill: Colours.green, r: 3}}
                            activeDot={{r: 5}} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </FlatContentCard>
            )}

            {officeChartData.length > 1 && (
              <FlatContentCard>
                <BodyLarge weight={600} style={{marginBottom: 12}}>{t('reports-page.car-park-utilization-by-office')}</BodyLarge>
                <div style={{height: 350}}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={officeChartData}
                              margin={{top: 20, right: 20, left: 0, bottom: 5}}>
                      <CartesianGrid stroke={Colours.lightGrey} strokeDasharray="5 5" />
                      <XAxis dataKey="name"
                             fontFamily={'"soleil", sans-serif'}
                             fill={Colours.darkGrey}
                             fontSize={12}
                             axisLine={false}
                             tickLine={false} />
                      <YAxis fontFamily={'"soleil", sans-serif'}
                             fontSize={12}
                             axisLine={false}
                             tickLine={false}
                             domain={[0, 100]}
                             tickFormatter={(v) => `${v}%`} />
                      <Tooltip content={<CustomToolTip />} />
                      <Bar dataKey="utilization" fill={Colours.blue} radius={[4, 4, 0, 0]}>
                        {officeChartData.map((entry, index) => (
                          <Cell key={index} fill={Colours.green} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </FlatContentCard>
            )}

            {bottomSpacesChartData.length > 0 && (
              <FlatContentCard style={{marginTop: 24}}>
                <BodyLarge weight={600} style={{marginBottom: 12}}>{t('reports-page.car-park-utilization-bottom-spaces')}</BodyLarge>
                <div style={{height: 350}}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={bottomSpacesChartData} layout="vertical"
                              margin={{top: 10, right: 20, left: 0, bottom: 5}}>
                      <CartesianGrid stroke={Colours.lightGrey} strokeDasharray="5 5" horizontal={false} />
                      <XAxis type="number"
                             fontFamily={'"soleil", sans-serif'}
                             fontSize={12}
                             axisLine={false}
                             tickLine={false}
                             domain={[0, 100]}
                             tickFormatter={(v) => `${v}%`} />
                      <YAxis type="category"
                             dataKey="name"
                             fontFamily={'"soleil", sans-serif'}
                             fontSize={11}
                             axisLine={false}
                             tickLine={false}
                             width={70} />
                      <Tooltip content={<CustomToolTip />} />
                      <Bar dataKey="utilization" fill={Colours.blue} radius={[0, 4, 4, 0]}>
                        {bottomSpacesChartData.map((entry, index) => (
                          <Cell key={index} fill={Colours.green} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </FlatContentCard>
            )}

            {reportData.offices.length > 0 && (
              <OfficeSection>
                <BodyLarge weight={600} style={{marginBottom: 12}}>{t('reports-page.car-park-utilization-office-breakdown')}</BodyLarge>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('reports-page.car-park-utilization-office')}</Th>
                      <Th>{t('reports-page.car-park-utilization-capacity')}</Th>
                      <Th>{t('reports-page.car-park-utilization-bookings')}</Th>
                      <Th>{t('reports-page.car-park-utilization-days')}</Th>
                      <Th>{t('reports-page.car-park-utilization-utilization')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.offices.map((o: OfficeCarParkUtilization) => (
                      <React.Fragment key={o.officeId}>
                        <ExpandableRow onClick={() => toggleOffice(o.officeId)}>
                          <Td>
                            <OfficeLabel>
                              <Arrow expanded={expandedOffices.has(o.officeId ?? -999)}>&#9654;</Arrow>
                              {o.officeLabel}
                            </OfficeLabel>
                          </Td>
                          <Td>{o.capacity}</Td>
                          <Td>{o.totalBookings}</Td>
                          <Td>{o.daysInRange}</Td>
                          <Td>{o.utilizationPercentage}%</Td>
                        </ExpandableRow>
                        {expandedOffices.has(o.officeId ?? -999) && o.spaces
                          ?.map(space => (
                          <SpaceRow key={space.spaceId}>
                            <Td><BodySmall style={{paddingLeft: 24}}>{space.label}</BodySmall></Td>
                            <Td>-</Td>
                            <Td>{space.totalBookings}</Td>
                            <Td>{space.daysInRange}</Td>
                            <Td>{space.utilizationPercentage}%</Td>
                          </SpaceRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </OfficeSection>
            )}

            {sortedDaily.length > 0 && (
              <OfficeSection>
                <BodyLarge weight={600} style={{marginBottom: 12}}>{t('reports-page.car-park-utilization-daily-breakdown')}</BodyLarge>
                <Table>
                  <thead>
                    <tr>
                      <SortableTh onClick={() => toggleDailySort('date')}>
                        {t('reports-page.car-park-utilization-date')}
                        <SortIndicator active={dailySortBy === 'date'}>{dailySortBy === 'date' ? (dailySortDirection === 'asc' ? ' ▲' : ' ▼') : ' ▲'}</SortIndicator>
                      </SortableTh>
                      <SortableTh onClick={() => toggleDailySort('capacity')}>
                        {t('reports-page.car-park-utilization-capacity')}
                        <SortIndicator active={dailySortBy === 'capacity'}>{dailySortBy === 'capacity' ? (dailySortDirection === 'asc' ? ' ▲' : ' ▼') : ' ▲'}</SortIndicator>
                      </SortableTh>
                      <SortableTh onClick={() => toggleDailySort('bookings')}>
                        {t('reports-page.car-park-utilization-bookings')}
                        <SortIndicator active={dailySortBy === 'bookings'}>{dailySortBy === 'bookings' ? (dailySortDirection === 'asc' ? ' ▲' : ' ▼') : ' ▲'}</SortIndicator>
                      </SortableTh>
                      <SortableTh onClick={() => toggleDailySort('utilizationPercentage')}>
                        {t('reports-page.car-park-utilization-utilization')}
                        <SortIndicator active={dailySortBy === 'utilizationPercentage'}>{dailySortBy === 'utilizationPercentage' ? (dailySortDirection === 'asc' ? ' ▲' : ' ▼') : ' ▲'}</SortIndicator>
                      </SortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDaily.map(day => (
                      <OfficeRow key={day.date}>
                        <Td>{moment(day.date).format('ddd DD MMM YYYY')}</Td>
                        <Td>{day.capacity}</Td>
                        <Td>{day.bookings}</Td>
                        <Td>{day.utilizationPercentage}%</Td>
                      </OfficeRow>
                    ))}
                  </tbody>
                </Table>
              </OfficeSection>
            )}
          </>
        )}
      </ReportingContainer>
    </>
  );
}

interface Props {
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 24px;
`

const ControlsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  @media (${tablet}) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`

const ControlGroup = styled.div`
  width: 100%;
  @media (${tablet}) {
    max-width: 320px;
  }
`

const DateRangeGroup = styled.div`
  width: 100%;
  .textField {
    margin-bottom: 0 !important;
  }
  @media (${tablet}) {
    max-width: 400px;
  }
`

const SummaryCard = styled.div`
  background-color: ${Colours.blue10};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`

const SummaryTitle = styled.h3`
  font-family: "soleil", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${Colours.black};
  margin: 0 0 16px 0;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  @media (${tablet}) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const SummaryItem = styled.div`
  text-align: center;
`

const SummaryValue = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${Colours.blue};
`

const SummaryLabel = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  color: ${Colours.darkGrey};
  margin-top: 4px;
`

const OfficeSection = styled.div`
  margin-top: 32px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const Th = styled.th`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${Colours.mildGrey};
`

const Td = styled.td`
  font-family: "soleil", sans-serif;
  font-size: 14px;
  color: ${Colours.black};
  padding: 10px 12px;
  border-bottom: 1px solid ${Colours.mildGrey};
`

const OfficeRow = styled.tr`
  &:hover {
    background-color: ${Colours.blue10};
  }
`

const ExpandableRow = styled.tr`
  cursor: pointer;
  &:hover {
    background-color: ${Colours.blue10};
  }
`

const SpaceRow = styled.tr`
  background-color: ${Colours.veryLightGrey};
`

const OfficeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`

const Arrow = styled.span<{ expanded: boolean }>`
  display: inline-block;
  font-size: 10px;
  transition: transform 0.2s;
  transform: ${props => props.expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`

const SortableTh = styled.th`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${Colours.mildGrey};
  cursor: pointer;
  user-select: none;
  &:hover {
    color: ${Colours.blue};
  }
`

const SortIndicator = styled.span<{ active: boolean }>`
  font-size: 10px;
  opacity: ${props => props.active ? 1 : 0.3};
`
